<template>
  <div class="p-6 flex flex-col h-full overflow-hidden gap-6">
    <div class="flex items-start justify-between gap-4 shrink-0">
      <div>
        <h2 class="text-xl font-semibold text-[var(--color-dark)]">
          {{ $t('ProjectManagement.title') }}
        </h2>
        <div class="text-sm text-[var(--color-disabled)]">
          {{ $t('ProjectManagement.subtitle') }}
        </div>
      </div>
    </div>

    <TabView v-model:activeIndex="activeTab" class="flex-1 flex flex-col min-h-0">
      <TabPanel :header="$t('ProjectManagement.tabs.projects')">
        <div class="flex justify-end gap-2 mb-4">
          <Button
            :label="$t('ProjectManagement.create')"
            icon="pi pi-plus"
            class="btn-primary-accent"
            unstyled
            data-testid="pm-create-project"
            @click="openCreateProjectDialog"
          />
          <Button
            :label="$t('ProjectManagement.refresh')"
            icon="pi pi-refresh"
            severity="secondary"
            :loading="projectLoading"
            data-testid="pm-refresh"
            @click="refreshProjects"
          />
        </div>

        <Message v-if="projectError" severity="error" :closable="false" class="mb-4">
          {{ projectError }}
        </Message>

        <div v-if="projectLoading" class="flex items-center justify-center py-16 text-[var(--color-disabled)]">
          <i class="pi pi-spin pi-spinner mr-2" />
          {{ $t('ProjectManagement.loading') }}
        </div>

        <div
          v-else-if="projectsListEmpty"
          class="surface-card border-round p-8 text-center text-[var(--color-disabled)]"
        >
          <i class="pi pi-briefcase text-3xl mb-3 block" />
          <div class="font-medium text-[var(--color-dark)]">{{ $t('ProjectManagement.emptyTitle') }}</div>
          <div class="text-sm mt-1">{{ $t('ProjectManagement.emptyHint') }}</div>
        </div>

        <DataTable
          v-else
          :value="projects"
          data-key="id"
          striped-rows
          paginator
          :rows="10"
          :rows-per-page-options="[10, 25, 50]"
          paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
          current-page-report-template="{first}-{last} / {totalRecords}"
          data-testid="pm-projects-table"
        >          <Column field="name" :header="$t('ProjectManagement.columns.name')" />
          <Column field="status" :header="$t('ProjectManagement.columns.status')" />
          <Column :header="$t('ProjectManagement.columns.lead')">
            <template #body="{ data }">
              {{ userDisplayName(data.projectLeadUserId) }}
            </template>
          </Column>
          <Column :header="$t('ProjectManagement.columns.teams')">
            <template #body="{ data }">
              {{ teamDisplayNames(data.teams) }}
            </template>
          </Column>
          <Column :header="$t('ProjectManagement.columns.actions')">
            <template #body="{ data }">
              <div class="flex gap-2">
                <Button
                  icon="pi pi-eye"
                  severity="secondary"
                  text
                  rounded
                  :aria-label="$t('ProjectManagement.viewer.open')"
                  data-testid="pm-view-project"
                  @click="openProjectViewer(data)"
                />
                <Button
                  icon="pi pi-list"
                  severity="info"
                  text
                  rounded
                  :aria-label="$t('ProjectManagement.commitments.title')"
                  @click="openCommitmentsForProject(data)"
                />
                <Button
                  icon="pi pi-pencil"
                  severity="secondary"
                  text
                  rounded
                  :aria-label="$t('ProjectManagement.edit')"
                  @click="openEditProjectDialog(data)"
                />
                <Button
                  icon="pi pi-check-circle"
                  severity="help"
                  text
                  rounded
                  :aria-label="$t('ProjectManagement.closure.openWizard')"
                  :pt="{ root: { 'data-testid': 'pm-open-closure-wizard' } }"
                  @click="openClosureWizard(data)"
                />
                <Button
                  icon="pi pi-file-excel"
                  severity="success"
                  text
                  rounded
                  :aria-label="$t('ProjectManagement.export.excel')"
                  :loading="exportingProjectId === data.id"
                  :pt="{ root: { 'data-testid': 'pm-export-project-excel' } }"
                  @click="exportProjectExcel(data)"
                />
                  <Button
                  icon="pi pi-trash"
                  severity="danger"
                  text
                  rounded
                  :disabled="data.status === 'archived'"
                  :aria-label="$t('ProjectManagement.archive')"
                  @click="confirmArchiveProject(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </TabPanel>

      <TabPanel :header="$t('ProjectManagement.tabs.pendingClosures')">
        <PendingClosuresPanel @changed="refreshProjects" />
      </TabPanel>

      <TabPanel :header="$t('ProjectManagement.tabs.archivedProjects')">
        <ArchivedProjectsPanel
          :user-display-name="userDisplayName"
          :team-display-names="teamDisplayNames"
        />
      </TabPanel>

      <TabPanel :header="$t('ProjectManagement.tabs.commitments')">
        <div class="flex flex-col sm:flex-row sm:items-end gap-4 mb-4">
          <div class="flex flex-col gap-1 flex-1">
            <label class="font-medium">{{ $t('ProjectManagement.context.label') }}</label>
            <Select
              v-model="selectedProjectId"
              :options="activeProjects"
              option-label="name"
              option-value="id"
              :placeholder="$t('ProjectManagement.context.placeholder')"
              class="w-full"
              data-testid="pm-project-context"
              @change="onProjectContextChange"
            />
          </div>
          <div class="flex gap-2">
            <Button
              :label="$t('ProjectManagement.commitments.create')"
              icon="pi pi-plus"
              class="btn-primary-accent"
              unstyled
              :disabled="!selectedProjectId"
              data-testid="pm-create-commitment"
              @click="openCreateCommitmentDialog"
            />
            <Button
              :label="$t('ProjectManagement.commitments.refresh')"
              icon="pi pi-refresh"
              severity="secondary"
              :loading="commitmentLoading"
              :disabled="!selectedProjectId"
              @click="refreshCommitments"
            />
          </div>
        </div>

        <Message v-if="!selectedProjectId" severity="info" :closable="false" class="mb-4">
          {{ $t('ProjectManagement.context.required') }}
        </Message>

        <Message v-if="commitmentError" severity="error" :closable="false" class="mb-4">
          {{ commitmentError }}
        </Message>

        <div
          v-if="selectedProjectId && commitmentLoading"
          class="flex items-center justify-center py-16 text-[var(--color-disabled)]"
        >
          <i class="pi pi-spin pi-spinner mr-2" />
          {{ $t('ProjectManagement.commitments.loading') }}
        </div>

        <div
          v-else-if="selectedProjectId && commitmentsEmpty"
          class="surface-card border-round p-8 text-center text-[var(--color-disabled)]"
        >
          <div class="font-medium text-[var(--color-dark)]">{{ $t('ProjectManagement.commitments.emptyTitle') }}</div>
          <div class="text-sm mt-1">{{ $t('ProjectManagement.commitments.emptyHint') }}</div>
        </div>

        <DataTable
          v-else-if="selectedProjectId"
          :value="commitments"
          data-key="id"
          striped-rows
        >
          <Column field="was" :header="$t('ProjectManagement.commitments.columns.was')" />
          <Column :header="$t('ProjectManagement.commitments.columns.owner')">
            <template #body="{ data }">
              {{ userDisplayName(data.ownerUserId) }}
            </template>
          </Column>
          <Column :header="$t('ProjectManagement.commitments.columns.milestone')">
            <template #body="{ data }">
              {{ milestoneLabel(data.milestoneId) }}
            </template>
          </Column>
          <Column field="dueDate" :header="$t('ProjectManagement.commitments.columns.dueDate')">
            <template #body="{ data }">
              {{ formatDate(data.dueDate) }}
            </template>
          </Column>
          <Column :header="$t('ProjectManagement.commitments.columns.status')">
            <template #body="{ data }">
              <Tag
                :value="$t(`ProjectManagement.commitments.status.${data.status}`)"
                :severity="commitmentStatusSeverity(data.status)"
              />
            </template>
          </Column>
          <Column :header="$t('ProjectManagement.commitments.columns.actions')">
            <template #body="{ data }">
              <div class="flex gap-1 flex-wrap">
                <Button
                  v-if="data.status === 'open'"
                  icon="pi pi-pencil"
                  severity="secondary"
                  text
                  rounded
                  @click="openEditCommitmentDialog(data)"
                />
                <Button
                  v-if="data.status === 'open'"
                  icon="pi pi-check"
                  severity="secondary"
                  text
                  rounded
                  class="text-[var(--color-orange)]"
                  :aria-label="$t('ProjectManagement.commitments.status.markDone')"
                  @click="markCommitmentDone(data)"
                />
                <Button
                  v-if="data.status === 'open'"
                  icon="pi pi-times"
                  severity="danger"
                  text
                  rounded
                  :aria-label="$t('ProjectManagement.commitments.status.markNotDone')"
                  @click="openCauseDialog(data)"
                />
                <Button
                  v-if="data.status === 'open'"
                  icon="pi pi-trash"
                  severity="danger"
                  text
                  rounded
                  @click="confirmDeleteCommitment(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </TabPanel>
    </TabView>

    <!-- Project dialog -->
    <Dialog
      v-model:visible="projectDialogVisible"
      modal
      :header="projectDialogMode === 'create' ? $t('ProjectManagement.createTitle') : $t('ProjectManagement.editTitle')"
      class="w-full max-w-lg"
      data-testid="pm-project-dialog"
    >
      <div class="flex flex-col gap-4" data-testid="pm-project-form">
        <label class="font-medium flex flex-col gap-1">
          {{ $t('ProjectManagement.form.name') }}
          <InputText
            v-model="projectForm.name"
            class="w-full"
            data-testid="pm-project-name"
            :aria-label="$t('ProjectManagement.form.name')"
            :invalid="!!projectFormErrors.name"
          />
          <small v-if="projectFormErrors.name" class="text-[var(--color-error)]">{{ projectFormErrors.name }}</small>
        </label>
        <label class="font-medium flex flex-col gap-1">
          {{ $t('ProjectManagement.form.description') }}
          <Textarea
            v-model="projectForm.description"
            class="w-full"
            rows="3"
            data-testid="pm-project-description"
            :aria-label="$t('ProjectManagement.form.description')"
          />
        </label>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label class="font-medium flex flex-col gap-1">
            {{ $t('ProjectManagement.form.startDate') }}
            <InputText
              v-model="projectForm.startDate"
              type="date"
              class="w-full"
              data-testid="pm-project-start-date"
              :aria-label="$t('ProjectManagement.form.startDate')"
            />
          </label>
          <label class="font-medium flex flex-col gap-1">
            {{ $t('ProjectManagement.form.endDate') }}
            <InputText
              v-model="projectForm.endDate"
              type="date"
              class="w-full"
              data-testid="pm-project-end-date"
              :aria-label="$t('ProjectManagement.form.endDate')"
            />
          </label>
        </div>
        <div class="flex flex-col gap-1">
          <label class="font-medium" id="pm-project-teams-label">{{ $t('ProjectManagement.form.linkedTeams') }}</label>
          <MultiSelect
            v-model="projectForm.selectedTeams"
            :options="teamsForSelection"
            option-label="name"
            display="chip"
            :placeholder="$t('ProjectManagement.form.linkedTeamsPlaceholder')"
            class="w-full"
            data-testid="pm-project-teams"
            aria-labelledby="pm-project-teams-label"
            :invalid="!!projectFormErrors.teams"
            @update:modelValue="onLinkedTeamsChange"
          />
          <small class="text-[var(--color-disabled)]">
            {{ $t('ProjectManagement.form.linkedTeamsHint') }}
          </small>
          <small v-if="projectFormErrors.teams" class="text-[var(--color-error)]">
            {{ projectFormErrors.teams }}
          </small>
        </div>
        <div class="flex flex-col gap-1">
          <label class="font-medium" id="pm-project-members-label">
            {{ $t('ProjectManagement.form.projectTeam') }}
          </label>
          <UserMultiSelectInput
            v-model="projectForm.memberUserIds"
            :users="participantUsers"
            :placeholder="$t('ProjectManagement.form.projectTeamPlaceholder')"
            :invalid="!!projectFormErrors.memberUserIds"
          />
          <small class="text-[var(--color-disabled)]">
            {{ $t('ProjectManagement.form.projectTeamHint') }}
          </small>
          <small v-if="projectFormErrors.memberUserIds" class="text-[var(--color-error)]">
            {{ projectFormErrors.memberUserIds }}
          </small>
        </div>
        <div class="flex flex-col gap-1">
          <label class="font-medium">{{ $t('ProjectManagement.form.lead') }}</label>
          <UserSelectInput
            v-model="projectForm.projectLeadUserId"
            :users="participantUsers"
            :invalid="!!projectFormErrors.projectLeadUserId"
          />
        </div>
        <div v-if="projectDialogMode === 'edit'" class="flex flex-col gap-1">
          <label class="font-medium" id="pm-project-status-label">{{ $t('ProjectManagement.form.status') }}</label>
          <Select
            v-model="projectForm.status"
            :options="projectStatusOptions"
            option-label="label"
            option-value="value"
            class="w-full"
            data-testid="pm-project-status"
            aria-labelledby="pm-project-status-label"
            :pt="{ root: { 'data-testid': 'pm-project-status', id: 'pm-project-status' } }"
          />
        </div>
      </div>
      <template #footer>
        <Button
          :label="$t('ProjectManagement.cancel')"
          severity="secondary"
          text
          data-testid="pm-project-cancel"
          @click="closeProjectDialog"
        />
        <Button
          :label="projectDialogMode === 'create' ? $t('ProjectManagement.create') : $t('ProjectManagement.save')"
          :loading="projectSaving"
          class="btn-primary-accent"
          unstyled
          data-testid="pm-project-submit"
          @click="submitProjectForm"
        />
      </template>
    </Dialog>

    <!-- Commitment dialog -->
    <CommitmentFormDialog
      v-model:visible="commitmentDialogVisible"
      :mode="commitmentDialogMode"
      :project-id="selectedProjectId"
      :project-name="selectedProjectName"
      :initial-commitment="editingCommitment"
      :participant-users="participantUsers"
      :saving="commitmentSaving"
      @submit="submitCommitmentDialog"
    />

    <ProjectClosureWizard
      v-model:visible="closureWizardVisible"
      :project="closureProject"
      @closed="onProjectClosed"
    />

    <!-- Cause dialog -->
    <Dialog
      v-model:visible="causeDialogVisible"
      modal
      :header="$t('ProjectManagement.commitments.cause.title')"
      class="w-full max-w-lg"
    >
      <p class="text-sm text-[var(--color-disabled)] mb-4">
        {{ $t('ProjectManagement.commitments.cause.subtitle') }}
      </p>
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <label class="font-medium">{{ $t('ProjectManagement.commitments.cause.category') }}</label>
          <Select
            v-model="causeForm.category"
            :options="causeCategoryOptions"
            option-label="label"
            option-value="value"
            class="w-full"
            :invalid="!!causeFormErrors.category"
          />
          <small v-if="causeFormErrors.category" class="text-[var(--color-error)]">
            {{ causeFormErrors.category }}
          </small>
        </div>
        <div class="flex flex-col gap-1">
          <label class="font-medium">{{ $t('ProjectManagement.commitments.cause.freeText') }}</label>
          <Textarea v-model="causeForm.freeText" class="w-full" rows="3" />
        </div>
        <div class="flex items-center gap-2">
          <Checkbox v-model="causeForm.constraintRequested" binary input-id="constraint-requested" />
          <label for="constraint-requested">{{ $t('ProjectManagement.commitments.cause.constraintRequested') }}</label>
        </div>
      </div>
      <template #footer>
        <Button :label="$t('ProjectManagement.cancel')" severity="secondary" text @click="closeCauseDialog" />
        <Button
          :label="$t('ProjectManagement.commitments.cause.submit')"
          :loading="causeSaving"
          class="btn-primary-accent"
          unstyled
          @click="submitCauseAndMarkNotDone"
        />
      </template>
    </Dialog>

    <ProjectViewerDialog
      v-model:visible="viewerVisible"
      :project="viewerProject"
      :teams="allTeams"
      :user-lookup="userLookup"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Message from 'primevue/message';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Tag from 'primevue/tag';
import Checkbox from 'primevue/checkbox';
import UserSelectInput from '@/components/global/UserSelectInput.vue';
import UserMultiSelectInput from '@/components/global/UserMultiSelectInput.vue';
import ProjectViewerDialog from '@/components/ContentManagement/views/ProjectViewerDialog.vue';
import ArchivedProjectsPanel from '@/components/ProjectManagement/ArchivedProjectsPanel.vue';
import ProjectClosureWizard from '@/components/ProjectManagement/ProjectClosureWizard.vue';
import PendingClosuresPanel from '@/components/ProjectManagement/PendingClosuresPanel.vue';
import CommitmentFormDialog from '@/components/CustomerApp/UserProjects/CommitmentFormDialog.vue';
import NetworkContent from '@/helpers/networking/NetworkContent';
import pmProjectExportService from '@/services/pmProjectExportService';
import pmProjectSettingsService from '@/services/pmProjectSettingsService';
import {
  buildUserLookup,
  getUserDisplayName,
  normalizeParticipantUsers,
} from '@/utils/userParticipants';
import {
  buildTeamUserMap,
  buildTeamUserMapFromTeams,
  suggestMembersFromTeams,
} from '@/utils/projectMemberMerge';

const EMPTY_PROJECT_FORM = () => ({
  name: '',
  description: '',
  startDate: '',
  endDate: '',
  projectLeadUserId: '',
  status: 'active',
  selectedTeams: [],
  memberUserIds: [],
});

const EMPTY_CAUSE_FORM = () => ({
  category: '',
  freeText: '',
  constraintRequested: false,
});

function toDateInputValue(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

export default {
  name: 'ProjectManagementView',
  components: {
    Button,
    DataTable,
    Column,
    Message,
    Dialog,
    InputText,
    Textarea,
    Select,
    MultiSelect,
    TabView,
    TabPanel,
    Tag,
    Checkbox,
    UserSelectInput,
    UserMultiSelectInput,
    ProjectViewerDialog,
    ArchivedProjectsPanel,
    ProjectClosureWizard,
    PendingClosuresPanel,
    CommitmentFormDialog,
  },
  setup() {
    return {
      confirm: useConfirm(),
      toast: useToast(),
    };
  },
  data() {
    return {
      activeTab: 0,
      selectedProjectId: null,
      projectDialogVisible: false,
      projectDialogMode: 'create',
      editingProjectId: null,
      projectSaving: false,
      projectForm: EMPTY_PROJECT_FORM(),
      projectFormErrors: {},
      commitmentDialogVisible: false,
      commitmentDialogMode: 'create',
      editingCommitmentId: null,
      editingCommitment: null,
      commitmentSaving: false,
      causeDialogVisible: false,
      causeSaving: false,
      causeTargetCommitment: null,
      causeForm: EMPTY_CAUSE_FORM(),
      causeFormErrors: {},
      participantUsers: [],
      usersLoading: false,
      viewerVisible: false,
      viewerProject: null,
      closureWizardVisible: false,
      closureProject: null,
      exportingProjectId: null,
      previousLinkedTeamIds: [],
    };
  },
  computed: {
    ...mapGetters('pmProject', {
      projects: 'projects',
      projectLoading: 'loading',
      projectError: 'error',
      projectsEmpty: 'isEmpty',
      activeProjects: 'activeProjects',
    }),
    ...mapGetters('pmCommitment', {
      commitments: 'commitments',
      commitmentLoading: 'loading',
      commitmentError: 'error',
      commitmentsEmpty: 'isEmpty',
    }),
    ...mapGetters('pmMilestone', {
      milestoneTitleById: 'milestoneTitleById',
    }),
    ...mapGetters({
      teamsForSelection: 'getTeamsForSelection',
      allTeams: 'getTeams',
    }),
    teamLookup() {
      const lookup = {};
      (this.teamsForSelection || []).forEach((team) => {
        lookup[String(team._id)] = team.name;
      });
      return lookup;
    },
    projectStatusOptions() {
      return [
        { label: this.$t('ProjectManagement.status.active'), value: 'active' },
        { label: this.$t('ProjectManagement.status.completed'), value: 'completed' },
      ];
    },
    causeCategoryOptions() {
      return ['blocked', 'resource', 'scope', 'other'].map((value) => ({
        value,
        label: this.$t(`ProjectManagement.commitments.cause.categories.${value}`),
      }));
    },
    userLookup() {
      return buildUserLookup(this.participantUsers);
    },
    activeProjectsEmpty() {
      return !this.projectLoading && !this.projectError && this.activeProjects.length === 0;
    },
    projectsListEmpty() {
      return !this.projectLoading && !this.projectError && this.projects.length === 0;
    },
    selectedProjectName() {
      const project = (this.activeProjects || []).find(
        (item) => String(item.id) === String(this.selectedProjectId),
      );
      return project?.name || '';
    },
  },
  async mounted() {
    await Promise.all([
      this.refreshProjects(),
      this.loadParticipantUsers(),
      this.$store.dispatch('getTeams'),
    ]);
  },
  methods: {
    ...mapActions('pmProject', [
      'fetchProjects',
      'createProject',
      'updateProject',
      'deleteProject',
      'selectProject',
    ]),
    ...mapActions('pmCommitment', {
      setCommitmentProjectContext: 'setProjectContext',
      fetchCommitments: 'fetchCommitments',
      createCommitment: 'createCommitment',
      updateCommitment: 'updateCommitment',
      deleteCommitment: 'deleteCommitment',
      updateCommitmentStatus: 'updateCommitmentStatus',
      recordCause: 'recordCause',
    }),
    ...mapActions('pmMilestone', {
      setMilestoneProjectContext: 'setProjectContext',
    }),

    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleDateString();
    },

    userDisplayName(userId) {
      if (!userId) return '—';
      return getUserDisplayName(this.userLookup[userId]) || userId;
    },

    teamDisplayNames(teamIds) {
      if (!Array.isArray(teamIds) || teamIds.length === 0) return '—';
      return teamIds
        .map((id) => this.teamLookup[String(id)] || String(id))
        .join(', ');
    },

    resolveSelectedTeams(teamIds) {
      if (!Array.isArray(teamIds) || teamIds.length === 0) return [];
      const idSet = new Set(teamIds.map(String));
      return (this.teamsForSelection || []).filter((team) => idSet.has(String(team._id)));
    },

    async loadParticipantUsers() {
      this.usersLoading = true;
      try {
        const { data } = await NetworkContent.getParticipants();
        this.participantUsers = normalizeParticipantUsers(data?.users ?? []);
      } catch {
        this.participantUsers = [];
      } finally {
        this.usersLoading = false;
      }
    },

    commitmentStatusSeverity(status) {
      if (status === 'done') return 'success';
      if (status === 'not_done') return 'danger';
      return 'info';
    },

    async refreshProjects() {
      try {
        await this.fetchProjects({ scope: 'all' });
      } catch {
        // handled in store
      }
    },

    async refreshCommitments() {
      if (!this.selectedProjectId) return;
      try {
        await this.fetchCommitments();
      } catch {
        // handled in store
      }
    },

    async onProjectContextChange() {
      this.selectProject(this.selectedProjectId);
      try {
        await this.setCommitmentProjectContext(this.selectedProjectId);
        if (this.selectedProjectId) {
          await this.setMilestoneProjectContext(this.selectedProjectId);
        }
      } catch {
        // handled in store
      }
    },

    milestoneLabel(milestoneId) {
      if (!milestoneId) return '—';
      return this.milestoneTitleById(milestoneId) || milestoneId;
    },

    openCommitmentsForProject(project) {
      this.selectedProjectId = project.id;
      this.activeTab = 2;
      this.onProjectContextChange();
    },

    openProjectViewer(project) {
      this.viewerProject = project;
      this.viewerVisible = true;
    },

    closeProjectViewer() {
      this.viewerVisible = false;
      this.viewerProject = null;
    },

    openCreateProjectDialog() {
      this.projectDialogMode = 'create';
      this.editingProjectId = null;
      this.projectForm = EMPTY_PROJECT_FORM();
      this.projectFormErrors = {};
      this.previousLinkedTeamIds = [];
      this.projectDialogVisible = true;
    },

    async openEditProjectDialog(project) {
      this.projectDialogMode = 'edit';
      this.editingProjectId = project.id;
      let memberUserIds = Array.isArray(project.memberUserIds)
        ? project.memberUserIds.map(String)
        : [];
      try {
        const members = await pmProjectSettingsService.listMembers(project.id);
        memberUserIds = (members.items || []).map((m) => String(m.userId));
      } catch {
        // keep memberUserIds from project payload if available
      }
      this.projectForm = {
        name: project.name || '',
        description: project.description || '',
        startDate: toDateInputValue(project.startDate),
        endDate: toDateInputValue(project.endDate),
        projectLeadUserId: project.projectLeadUserId || '',
        status: project.status || 'active',
        selectedTeams: this.resolveSelectedTeams(project.teams),
        memberUserIds,
      };
      this.previousLinkedTeamIds = (project.teams || []).map(String);
      this.projectFormErrors = {};
      this.projectDialogVisible = true;
    },

    onLinkedTeamsChange(nextTeams) {
      const nextIds = (nextTeams || []).map((t) => String(t._id));
      const prevIds = this.previousLinkedTeamIds || [];
      const newlyAdded = nextIds.filter((id) => !prevIds.includes(id));
      if (newlyAdded.length) {
        // Prefer team.members from CMS; fall back to participants' team memberships.
        const fromTeams = buildTeamUserMapFromTeams(this.teamsForSelection || []);
        const fromUsers = buildTeamUserMap(this.participantUsers || []);
        const teamUserMap = { ...fromUsers };
        Object.keys(fromTeams).forEach((teamId) => {
          teamUserMap[teamId] = [
            ...new Set([...(fromUsers[teamId] || []), ...(fromTeams[teamId] || [])]),
          ];
        });
        this.projectForm.memberUserIds = suggestMembersFromTeams(
          newlyAdded,
          teamUserMap,
          this.projectForm.memberUserIds || [],
        );
      }
      this.previousLinkedTeamIds = nextIds;
    },
    closeProjectDialog() {
      this.projectDialogVisible = false;
      this.projectSaving = false;
      this.projectFormErrors = {};
    },

    openClosureWizard(project) {
      this.closureProject = project;
      this.closureWizardVisible = true;
    },

    async exportProjectExcel(project) {
      if (!project?.id) return;
      this.exportingProjectId = project.id;
      try {
        await pmProjectExportService.downloadProjectExcel(project.id, project.name);
        this.toast.add({
          severity: 'success',
          summary: this.$t('ProjectManagement.export.toastSuccess'),
          life: 3000,
        });
      } catch (error) {
        this.toast.add({
          severity: 'error',
          summary: error?.response?.data?.message
            || error?.message
            || this.$t('ProjectManagement.export.toastError'),
          life: 4000,
        });
      } finally {
        this.exportingProjectId = null;
      }
    },

    async onProjectClosed() {
      this.toast.add({
        severity: 'success',
        summary: this.$t('ProjectManagement.closure.toastClosed'),
        life: 3000,
      });
      await this.refreshProjects();
    },

    validateProjectForm() {
      const errors = {};
      if (!this.projectForm.name.trim()) {
        errors.name = this.$t('ProjectManagement.validation.nameRequired');
      }
      if (!this.projectForm.selectedTeams?.length) {
        errors.teams = this.$t('ProjectManagement.validation.teamsRequired');
      }
      if (!this.projectForm.memberUserIds?.length) {
        errors.memberUserIds = this.$t('ProjectManagement.validation.projectTeamRequired');
      }
      const leadId = this.projectForm.projectLeadUserId?.trim();
      if (leadId && !(this.projectForm.memberUserIds || []).map(String).includes(leadId)) {
        errors.projectLeadUserId = this.$t('ProjectManagement.validation.leadMustBeMember');
      }
      this.projectFormErrors = errors;
      return Object.keys(errors).length === 0;
    },

    async submitProjectForm() {
      if (!this.validateProjectForm()) return;
      this.projectSaving = true;
      const teams = (this.projectForm.selectedTeams || []).map((team) => String(team._id));
      const memberUserIds = [...new Set((this.projectForm.memberUserIds || []).map(String))];
      const payload = {
        name: this.projectForm.name.trim(),
        description: this.projectForm.description?.trim() || undefined,
        projectLeadUserId: this.projectForm.projectLeadUserId?.trim() || undefined,
        teams,
        memberUserIds,
      };
      if (this.projectForm.startDate) payload.startDate = new Date(this.projectForm.startDate).toISOString();
      if (this.projectForm.endDate) payload.endDate = new Date(this.projectForm.endDate).toISOString();
      if (this.projectDialogMode === 'edit') payload.status = this.projectForm.status;

      try {
        if (this.projectDialogMode === 'create') {
          await this.createProject(payload);
          this.toast.add({ severity: 'success', summary: this.$t('ProjectManagement.toast.created'), life: 3000 });
        } else {
          await this.updateProject({ id: this.editingProjectId, payload });
          this.toast.add({ severity: 'success', summary: this.$t('ProjectManagement.toast.updated'), life: 3000 });
        }
        this.closeProjectDialog();
        await this.refreshProjects();
      } catch {
        this.toast.add({ severity: 'error', summary: this.$t('ProjectManagement.toast.error'), life: 4000 });
      } finally {
        this.projectSaving = false;
      }
    },

    confirmArchiveProject(project) {
      this.confirm.require({
        message: this.$t('ProjectManagement.archiveConfirm', { name: project.name }),
        header: this.$t('ProjectManagement.archiveTitle'),
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: async () => {
          try {
            await this.deleteProject(project.id);
            if (this.selectedProjectId === project.id) {
              this.selectedProjectId = null;
              await this.setCommitmentProjectContext(null);
            }
            this.toast.add({ severity: 'success', summary: this.$t('ProjectManagement.toast.archived'), life: 3000 });
          } catch {
            this.toast.add({ severity: 'error', summary: this.$t('ProjectManagement.toast.error'), life: 4000 });
          }
        },
      });
    },

    openCreateCommitmentDialog() {
      this.commitmentDialogMode = 'create';
      this.editingCommitmentId = null;
      this.editingCommitment = null;
      this.commitmentDialogVisible = true;
    },

    openEditCommitmentDialog(commitment) {
      this.commitmentDialogMode = 'edit';
      this.editingCommitmentId = commitment.id;
      this.editingCommitment = commitment;
      this.commitmentDialogVisible = true;
    },

    closeCommitmentDialog() {
      this.commitmentDialogVisible = false;
      this.commitmentSaving = false;
      this.editingCommitment = null;
    },

    async submitCommitmentDialog(payload) {
      this.commitmentSaving = true;
      try {
        if (this.commitmentDialogMode === 'create') {
          await this.createCommitment(payload);
          this.toast.add({ severity: 'success', summary: this.$t('ProjectManagement.commitments.toast.created'), life: 3000 });
        } else {
          await this.updateCommitment({ id: this.editingCommitmentId, payload });
          this.toast.add({ severity: 'success', summary: this.$t('ProjectManagement.commitments.toast.updated'), life: 3000 });
        }
        this.closeCommitmentDialog();
        await this.refreshCommitments();
      } catch {
        this.toast.add({ severity: 'error', summary: this.$t('ProjectManagement.commitments.toast.error'), life: 4000 });
      } finally {
        this.commitmentSaving = false;
      }
    },

    async markCommitmentDone(commitment) {
      try {
        await this.updateCommitmentStatus({ id: commitment.id, status: 'done' });
        this.toast.add({ severity: 'success', summary: this.$t('ProjectManagement.commitments.toast.statusUpdated'), life: 3000 });
      } catch {
        this.toast.add({ severity: 'error', summary: this.$t('ProjectManagement.commitments.toast.error'), life: 4000 });
      }
    },

    openCauseDialog(commitment) {
      this.causeTargetCommitment = commitment;
      this.causeForm = EMPTY_CAUSE_FORM();
      this.causeFormErrors = {};
      this.causeDialogVisible = true;
    },

    closeCauseDialog() {
      this.causeDialogVisible = false;
      this.causeSaving = false;
      this.causeTargetCommitment = null;
      this.causeFormErrors = {};
    },

    validateCauseForm() {
      const errors = {};
      if (!this.causeForm.category) {
        errors.category = this.$t('ProjectManagement.commitments.validation.causeCategoryRequired');
      }
      this.causeFormErrors = errors;
      return Object.keys(errors).length === 0;
    },

    async submitCauseAndMarkNotDone() {
      if (!this.validateCauseForm() || !this.causeTargetCommitment) return;
      this.causeSaving = true;
      try {
        await this.recordCause({
          id: this.causeTargetCommitment.id,
          payload: {
            category: this.causeForm.category,
            freeText: this.causeForm.freeText?.trim() || undefined,
            constraintRequested: this.causeForm.constraintRequested,
          },
        });
        await this.updateCommitmentStatus({
          id: this.causeTargetCommitment.id,
          status: 'not_done',
        });
        this.toast.add({ severity: 'success', summary: this.$t('ProjectManagement.commitments.toast.causeRecorded'), life: 3000 });
        this.closeCauseDialog();
      } catch {
        this.toast.add({ severity: 'error', summary: this.$t('ProjectManagement.commitments.toast.error'), life: 4000 });
      } finally {
        this.causeSaving = false;
      }
    },

    confirmDeleteCommitment(commitment) {
      this.confirm.require({
        message: this.$t('ProjectManagement.commitments.deleteConfirm', { name: commitment.was }),
        header: this.$t('ProjectManagement.commitments.deleteTitle'),
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: async () => {
          try {
            await this.deleteCommitment(commitment.id);
            this.toast.add({ severity: 'success', summary: this.$t('ProjectManagement.commitments.toast.deleted'), life: 3000 });
          } catch {
            this.toast.add({ severity: 'error', summary: this.$t('ProjectManagement.commitments.toast.error'), life: 4000 });
          }
        },
      });
    },
  },
};
</script>
